import {
  DEFAULT_DISPLAY_NAME,
  ENGAGEMENT_CATALOG,
  ESCALATION_CATALOG,
  MAX_DISPLAY_NAME_LENGTH,
  MAX_RULES,
  MAX_RULE_TEXT_LENGTH,
  MIN_DISPLAY_NAME_LENGTH,
  confidencePostures,
  greetingTemplates,
  proactivenessLevels,
  toneProfiles,
  upsellPostures,
  type GreetingTemplate,
  type ToneProfile,
} from 'skipify-types';
import isEqual from 'fast-deep-equal/es6/react';

import {
  AgenticAgentSettings,
  AgenticRoomScenes,
  AgenticAgentSettingsCreate,
  AgenticAgentSettingsPatch,
  AgenticEscalationSettings,
  AgenticKnowledgeBaseSettings,
  AgenticPersonality,
  AgenticPersonalityCreate,
  AgenticPersonalityPatch,
  AgenticPronunciationEntry,
  AgenticSupportSettings,
  AgentSelectorMobileSettings,
  EllaRule,
} from '../../store/modules/merchant/types';
import { isValidHandoffUrl, isValidOrderCompleteUrl } from '../../util/helpers';
import { DEFAULT_AGENT_AVATAR_SRC, DEFAULT_AGENT_VOICE_PROFILE_ID } from './agentCatalog';
import {
  isAgentSelectorHexValid,
  mobileLabelTextForStorage,
  normalizeMobileLabelTextFromServer,
  toAgentSelector,
} from './agentSelector';
import { getDefaultEllaCreateSettings } from './defaults';
import { EMPTY_ROOM_SCENES, isRoomScenesValid } from './roomScenes';

/** Local draft row for an agent personality (saved or newly created in this session). */
export type EllaPersonalityDraft = {
  clientId: string;
  personalityId?: string;
  isNew: boolean;
  displayName: string;
  avatarSrc: string;
  tone: ToneProfile;
  voiceProfileId: string;
  enabled: boolean;
};

/** Writable Ella agent settings used as the single draft/baseline source of truth. */
export type EllaAgentDraft = Omit<AgenticAgentSettingsCreate, 'personalities'> & {
  personalities?: EllaPersonalityDraft[];
};

const toGreeting = (customGreeting?: string): GreetingTemplate =>
  customGreeting && (greetingTemplates as readonly string[]).includes(customGreeting)
    ? (customGreeting as GreetingTemplate)
    : 'shopping-concierge';

const toGlossary = (entries?: AgenticPronunciationEntry[]): AgenticPronunciationEntry[] => {
  if (!entries || entries.length === 0) {
    return [{ text: '', pronunciation: '' }];
  }
  return entries.map((entry) => ({
    text: entry.text ?? '',
    pronunciation: entry.pronunciation ?? '',
  }));
};

const completeGlossaryEntries = (entries: AgenticPronunciationEntry[]) =>
  entries
    .map((entry) => ({
      text: entry.text.trim(),
      pronunciation: entry.pronunciation.trim(),
    }))
    .filter((entry) => entry.text.length > 0 && entry.pronunciation.length > 0);

const toEscalation = (escalation?: AgenticEscalationSettings): AgenticEscalationSettings => {
  const payload: AgenticEscalationSettings = {
    threshold: escalation?.threshold ?? ESCALATION_CATALOG.escalationThreshold.default,
    operatingHours: escalation?.operatingHours ?? 'business-hours',
    instruction:
      escalation?.instruction ??
      'Direct the shopper to call our support phone number for assistance',
  };
  if (escalation?.contactPhoneNumber?.trim()) {
    payload.contactPhoneNumber = escalation.contactPhoneNumber.trim();
  }
  if (escalation?.contactEmail?.trim()) {
    payload.contactEmail = escalation.contactEmail.trim();
  }
  if (escalation?.contactUrl?.trim()) {
    payload.contactUrl = escalation.contactUrl.trim();
  }
  return payload;
};

const toSupport = (support?: AgenticSupportSettings): AgenticSupportSettings => ({
  enabled: support?.enabled ?? false,
});

const toKnowledgeBase = (
  knowledgeBase?: AgenticKnowledgeBaseSettings,
): AgenticKnowledgeBaseSettings => ({
  enabled: knowledgeBase?.enabled ?? false,
});

/** Draft/baseline shape: keep mode-specific URLs even when the active mode hides them (stale MMS values). */
const toCheckoutModeDraft = (
  checkoutMode?: AgenticAgentSettings['checkoutMode'] | EllaAgentDraft['checkoutMode'],
): EllaAgentDraft['checkoutMode'] => {
  const mode = checkoutMode?.mode ?? 'inline';
  const next: EllaAgentDraft['checkoutMode'] = { mode };
  if (checkoutMode?.handoffUrl?.trim()) {
    next.handoffUrl = checkoutMode.handoffUrl.trim();
  }
  if (checkoutMode?.orderCompleteUrl?.trim()) {
    next.orderCompleteUrl = checkoutMode.orderCompleteUrl.trim();
  }
  return next;
};

/** API create/PATCH shape: only URLs valid for the selected mode. */
const toCheckoutModePayload = (
  checkoutMode?: EllaAgentDraft['checkoutMode'],
): EllaAgentDraft['checkoutMode'] => {
  const mode = checkoutMode?.mode ?? 'inline';
  const next: EllaAgentDraft['checkoutMode'] = { mode };
  if (mode === 'handoff' && checkoutMode?.handoffUrl?.trim()) {
    next.handoffUrl = checkoutMode.handoffUrl.trim();
  }
  if (mode === 'inline' && checkoutMode?.orderCompleteUrl?.trim()) {
    next.orderCompleteUrl = checkoutMode.orderCompleteUrl.trim();
  }
  return next;
};

const toRules = (rules?: EllaRule[]): EllaRule[] =>
  (rules ?? []).map((rule) => {
    const text = rule.text.trim();
    const name = (rule.name ?? '').trim() || text;
    return {
      name,
      text,
      enabled: rule.enabled,
    };
  });

const isRuleValid = (rule: EllaRule): boolean => {
  const name = (rule.name ?? '').trim();
  const text = rule.text.trim();
  if (name.length === 0) {
    return false;
  }
  if (text.length === 0 || text.length > MAX_RULE_TEXT_LENGTH) {
    return false;
  }
  return true;
};

const pickEnum = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

const toEngagement = (
  engagement?: AgenticAgentSettings['engagement'],
): EllaAgentDraft['engagement'] => {
  const intrusiveness = engagement?.intrusiveness;
  return {
    intrusiveness:
      typeof intrusiveness === 'number' &&
      Number.isInteger(intrusiveness) &&
      intrusiveness >= ENGAGEMENT_CATALOG.intrusiveness.min &&
      intrusiveness <= ENGAGEMENT_CATALOG.intrusiveness.max
        ? intrusiveness
        : ENGAGEMENT_CATALOG.intrusiveness.default,
    upsellPosture: pickEnum(engagement?.upsellPosture, upsellPostures, 'balanced'),
    confidencePosture: pickEnum(engagement?.confidencePosture, confidencePostures, 'standard'),
    proactiveness: pickEnum(engagement?.proactiveness, proactivenessLevels, 'balanced'),
  };
};

/** MMS: optional `{ right, bottom }` integers (may be negative). Both required when set. */
export const isWidgetPositionValid = (
  widgetPosition: { right: number; bottom: number } | undefined,
): boolean => {
  if (!widgetPosition) {
    return true;
  }
  return Number.isInteger(widgetPosition.right) && Number.isInteger(widgetPosition.bottom);
};

/** Scenes only apply to the full-page view: under another display they stay in the draft, but aren't validated or saved. */
const activeRoomScenes = ({
  productDisplay,
  roomScenes,
}: EllaAgentDraft['productExperience']): AgenticRoomScenes | undefined =>
  productDisplay === 'StoreFront' ? roomScenes : undefined;

/** Scenes are sent whole, and only when the merchant could see the form they changed them in. */
const changedRoomScenes = (
  baseline: EllaAgentDraft,
  draft: EllaAgentDraft,
): AgenticRoomScenes | undefined => {
  if (draft.productExperience.productDisplay !== 'StoreFront') {
    return undefined;
  }
  const scenes = draft.productExperience.roomScenes ?? EMPTY_ROOM_SCENES;
  return isEqual(baseline.productExperience.roomScenes ?? EMPTY_ROOM_SCENES, scenes)
    ? undefined
    : scenes;
};

const withoutRoomScenes = (
  productExperience: EllaAgentDraft['productExperience'],
): EllaAgentDraft['productExperience'] => {
  const rest = { ...productExperience };
  delete rest.roomScenes;
  return rest;
};

const toProductExperience = (
  productExperience: EllaAgentDraft['productExperience'],
): EllaAgentDraft['productExperience'] => {
  const agentSelector = toAgentSelector(productExperience.agentSelector);
  const agentSelectorForSave = {
    ...agentSelector,
    mobile: {
      ...agentSelector.mobile,
      labelText: mobileLabelTextForStorage(agentSelector.mobile.labelText),
    },
  };
  const roomScenes = productExperience.roomScenes
    ? { roomScenes: productExperience.roomScenes }
    : {};
  const widgetPosition = productExperience.widgetPosition;
  if (!widgetPosition || !isWidgetPositionValid(widgetPosition)) {
    return {
      productDisplay: productExperience.productDisplay,
      agentSelector: agentSelectorForSave,
      ...roomScenes,
    };
  }
  return {
    productDisplay: productExperience.productDisplay,
    agentSelector: agentSelectorForSave,
    ...roomScenes,
    widgetPosition: {
      right: widgetPosition.right,
      bottom: widgetPosition.bottom,
    },
  };
};

/** After PATCH, MMS may omit slices the client just saved; keep local values when absent on the response. */
export const mergeProductExperienceAfterSave = (
  local: EllaAgentDraft['productExperience'],
  saved: AgenticAgentSettings['productExperience'],
): EllaAgentDraft['productExperience'] => {
  const merged: EllaAgentDraft['productExperience'] = {
    ...local,
    productDisplay: saved.productDisplay,
  };
  if (saved.agentSelector !== undefined) {
    merged.agentSelector = toAgentSelector(saved.agentSelector);
  }
  if (saved.roomScenes !== undefined) {
    merged.roomScenes = saved.roomScenes;
  }
  if (
    saved.widgetPosition !== undefined &&
    saved.widgetPosition &&
    isWidgetPositionValid(saved.widgetPosition)
  ) {
    merged.widgetPosition = saved.widgetPosition;
  }
  return toProductExperience(merged);
};

export const toPersonalityCreatePayload = (
  personality: EllaPersonalityDraft,
): AgenticPersonalityCreate => ({
  displayName: personality.displayName.trim(),
  avatar: { src: personality.avatarSrc },
  tone: personality.tone,
  voice: { profileId: personality.voiceProfileId },
});

export const newPersonalityPayloads = (
  personalities: EllaPersonalityDraft[] | undefined,
): AgenticPersonalityCreate[] =>
  (personalities ?? []).filter((personality) => personality.isNew).map(toPersonalityCreatePayload);

const personalityIdentity = (personality: EllaPersonalityDraft) => ({
  personalityId: personality.personalityId ?? null,
  isNew: personality.isNew,
  displayName: personality.displayName.trim(),
  avatarSrc: personality.avatarSrc,
  tone: personality.tone,
  voiceProfileId: personality.voiceProfileId,
});

const toPersonalitiesPatch = (
  baseline: EllaPersonalityDraft[] | undefined,
  draft: EllaPersonalityDraft[] | undefined,
): AgenticPersonalityPatch[] | undefined => {
  const baselineByClientId = new Map(
    (baseline ?? []).map((personality) => [personality.clientId, personality]),
  );
  const items: AgenticPersonalityPatch[] = [];

  for (const personality of draft ?? []) {
    if (personality.isNew) {
      items.push(toPersonalityCreatePayload(personality));
      continue;
    }

    const previous = baselineByClientId.get(personality.clientId);
    if (!personality.personalityId) {
      if (!previous || !isEqual(personalityIdentity(previous), personalityIdentity(personality))) {
        items.push(toPersonalityCreatePayload(personality));
      }
      continue;
    }

    if (!previous) {
      items.push({
        personalityId: personality.personalityId,
        ...toPersonalityCreatePayload(personality),
      });
      continue;
    }

    const patch: AgenticPersonalityPatch = { personalityId: personality.personalityId };
    if (previous.displayName.trim() !== personality.displayName.trim()) {
      patch.displayName = personality.displayName.trim();
    }
    if (previous.avatarSrc !== personality.avatarSrc) {
      patch.avatar = { src: personality.avatarSrc };
    }
    if (previous.tone !== personality.tone) {
      patch.tone = personality.tone;
    }
    if (previous.voiceProfileId !== personality.voiceProfileId) {
      patch.voice = { profileId: personality.voiceProfileId };
    }

    if (Object.keys(patch).length > 1) {
      items.push(patch);
    }
  }

  return items.length > 0 ? items : undefined;
};

const personalityFromSettings = (
  personality: AgenticPersonality,
  defaultVoiceProfileId: string,
): EllaPersonalityDraft => ({
  clientId: personality.personalityId,
  personalityId: personality.personalityId,
  isNew: false,
  displayName: personality.displayName || DEFAULT_DISPLAY_NAME,
  avatarSrc: personality.avatar?.src || DEFAULT_AGENT_AVATAR_SRC,
  tone: pickEnum(personality.tone, toneProfiles, 'warm-assured'),
  voiceProfileId: String(personality.voice?.profileId || defaultVoiceProfileId),
  enabled: personality.enabled,
});

export const defaultPersonalityFromPrompt = (options?: {
  isNew?: boolean;
  displayName?: string;
  tone?: ToneProfile;
  voiceProfileId?: string;
}): EllaPersonalityDraft => ({
  clientId: 'default-ella',
  isNew: options?.isNew ?? false,
  displayName: options?.displayName || DEFAULT_DISPLAY_NAME,
  avatarSrc: DEFAULT_AGENT_AVATAR_SRC,
  tone: pickEnum(options?.tone, toneProfiles, 'warm-assured'),
  voiceProfileId: options?.voiceProfileId || DEFAULT_AGENT_VOICE_PROFILE_ID,
  // Personality live status is independent of Ella being enabled.
  enabled: false,
});

/** Default in-memory draft, including the unsaved Enable Ella personality. */
export const getDefaultEllaDraft = (
  overrides?: Partial<Omit<AgenticAgentSettingsCreate, 'personalities'>>,
  defaultVoiceProfileId: string = DEFAULT_AGENT_VOICE_PROFILE_ID,
  hasDefaultPaymentCredential?: boolean,
): EllaAgentDraft => ({
  ...getDefaultEllaCreateSettings(overrides, { hasDefaultPaymentCredential }),
  personalities: [
    defaultPersonalityFromPrompt({ isNew: true, voiceProfileId: defaultVoiceProfileId }),
  ],
});

const toPersonalities = (
  settings: AgenticAgentSettings,
  defaultVoiceProfileId: string,
): EllaPersonalityDraft[] =>
  (settings.personalities ?? [])
    .filter((personality) => !personality.isDeleted)
    .map((personality) => personalityFromSettings(personality, defaultVoiceProfileId));

/** Copy server personality ids onto the draft after save. Keeps local `clientId`. */
export const mergeSavedPersonalityIds = (
  draftPersonalities: EllaPersonalityDraft[] | undefined,
  savedPersonalities: AgenticPersonality[] | undefined,
): EllaPersonalityDraft[] => {
  const leftover = (savedPersonalities ?? []).filter((personality) => !personality.isDeleted);
  const claimedIds = new Set(
    (draftPersonalities ?? [])
      .map((personality) => personality.personalityId)
      .filter((id): id is string => Boolean(id)),
  );

  return (draftPersonalities ?? []).map((personality) => {
    const matchIndex = personality.personalityId
      ? leftover.findIndex((candidate) => candidate.personalityId === personality.personalityId)
      : leftover.findIndex((candidate) => !claimedIds.has(candidate.personalityId));
    const match = matchIndex >= 0 ? leftover.splice(matchIndex, 1)[0] : undefined;

    if (!match) {
      return { ...personality, isNew: false };
    }

    return {
      ...personality,
      isNew: false,
      personalityId: match.personalityId,
      enabled: match.enabled,
    };
  });
};

/** Keep local `clientId`s when GET rebuilds rows with `clientId = personalityId`. */
export const preservePersonalityClientIds = (
  previous: EllaPersonalityDraft[] | undefined,
  next: EllaPersonalityDraft[] | undefined,
): EllaPersonalityDraft[] => {
  const prevById = new Map(
    (previous ?? [])
      .filter((personality) => personality.personalityId)
      .map((personality) => [personality.personalityId as string, personality.clientId]),
  );
  const leftoverNew = (previous ?? []).filter((personality) => !personality.personalityId);

  return (next ?? []).map((personality) => {
    const knownId = personality.personalityId && prevById.get(personality.personalityId);
    const clientId = knownId || leftoverNew.shift()?.clientId;
    return clientId ? { ...personality, clientId } : personality;
  });
};

const personalitiesForCompare = (personalities: EllaPersonalityDraft[] | undefined) =>
  (personalities ?? []).map(personalityIdentity);

/** Map GET agent settings into the writable draft shape. */
export const settingsToDraft = (
  settings: AgenticAgentSettings,
  defaultVoiceProfileId: string = DEFAULT_AGENT_VOICE_PROFILE_ID,
): EllaAgentDraft => {
  const personalities = toPersonalities(settings, defaultVoiceProfileId);

  return {
    personalities,
    promptManagement: {
      formality: settings.promptManagement.formality,
      sentenceStyle: settings.promptManagement.sentenceStyle,
      customGreeting: toGreeting(settings.promptManagement.customGreeting),
      brandVocabulary: {
        mirrorSiteTerminology:
          settings.promptManagement.brandVocabulary?.mirrorSiteTerminology ?? true,
      },
      suggestedPrompts: settings.promptManagement.suggestedPrompts ?? [],
    },
    voice: {
      pronunciationGlossary: toGlossary(settings.voice?.pronunciationGlossary),
    },
    productExperience: {
      productDisplay: settings.productExperience.productDisplay,
      agentSelector: (() => {
        const agentSelector = toAgentSelector(settings.productExperience.agentSelector);
        return {
          ...agentSelector,
          mobile: {
            ...agentSelector.mobile,
            labelText: normalizeMobileLabelTextFromServer(
              settings.productExperience.agentSelector?.mobile?.labelText,
            ),
          },
        };
      })(),
      ...(settings.productExperience.widgetPosition &&
      isWidgetPositionValid(settings.productExperience.widgetPosition)
        ? { widgetPosition: settings.productExperience.widgetPosition }
        : {}),
      ...(settings.productExperience.roomScenes
        ? { roomScenes: settings.productExperience.roomScenes }
        : {}),
    },
    engagement: toEngagement(settings.engagement),
    escalation: toEscalation(settings.escalation),
    checkoutMode: toCheckoutModeDraft(settings.checkoutMode),
    rules: toRules(settings.rules),
    support: toSupport(settings.support),
    knowledgeBase: toKnowledgeBase(settings.knowledgeBase),
  };
};

const toPromptManagementPayload = (
  promptManagement: EllaAgentDraft['promptManagement'],
): AgenticAgentSettingsCreate['promptManagement'] => ({
  formality: promptManagement.formality,
  sentenceStyle: promptManagement.sentenceStyle,
  customGreeting: promptManagement.customGreeting,
  brandVocabulary: {
    mirrorSiteTerminology: promptManagement.brandVocabulary.mirrorSiteTerminology,
  },
  suggestedPrompts: promptManagement.suggestedPrompts ?? [],
});

const productExperienceForCompare = (
  productExperience: EllaAgentDraft['productExperience'],
): EllaAgentDraft['productExperience'] => {
  // Compared on their own by `changedRoomScenes`, as whether they count depends on the display.
  const normalized = withoutRoomScenes(toProductExperience(productExperience));
  const rawMobileLabel = productExperience.agentSelector?.mobile?.labelText;
  const normalizedForCompare =
    rawMobileLabel !== undefined && !rawMobileLabel.trim()
      ? {
          ...normalized,
          agentSelector: {
            ...normalized.agentSelector!,
            mobile: {
              ...normalized.agentSelector!.mobile,
              labelText: rawMobileLabel,
            },
          },
        }
      : normalized;
  const rawPosition = productExperience.widgetPosition;
  if (rawPosition && !isWidgetPositionValid(rawPosition)) {
    return { ...normalizedForCompare, widgetPosition: rawPosition };
  }
  return normalizedForCompare;
};

const draftForCompare = (draft: EllaAgentDraft) => ({
  ...draft,
  promptManagement: toPromptManagementPayload(draft.promptManagement),
  voice: {
    pronunciationGlossary: completeGlossaryEntries(draft.voice?.pronunciationGlossary ?? []),
  },
  productExperience: productExperienceForCompare(draft.productExperience),
  engagement: toEngagement(draft.engagement),
  escalation: toEscalation(draft.escalation),
  checkoutMode: toCheckoutModeDraft(draft.checkoutMode),
  rules: toRules(draft.rules),
  support: toSupport(draft.support),
  knowledgeBase: toKnowledgeBase(draft.knowledgeBase),
  personalities: personalitiesForCompare(draft.personalities),
});

export const isDraftDirty = (baseline: EllaAgentDraft, draft: EllaAgentDraft): boolean =>
  !isEqual(draftForCompare(baseline), draftForCompare(draft)) ||
  changedRoomScenes(baseline, draft) !== undefined;

const isOptionalHandoffUrlValid = (urlString: string): boolean => {
  const trimmed = urlString.trim();
  return !trimmed || isValidHandoffUrl(trimmed);
};

const isOptionalOrderCompleteUrlValid = (urlString: string): boolean => {
  const trimmed = urlString.trim();
  return !trimmed || isValidOrderCompleteUrl(trimmed);
};

export const isDraftValid = (draft: EllaAgentDraft): boolean => {
  const glossary = draft.voice?.pronunciationGlossary ?? [];
  const hasIncompletePronunciation = glossary.some((entry) => {
    const text = entry.text.trim();
    const pronunciation = entry.pronunciation.trim();
    return (
      (text.length > 0 && pronunciation.length === 0) ||
      (pronunciation.length > 0 && text.length === 0)
    );
  });
  if (hasIncompletePronunciation) {
    return false;
  }

  const seen = new Set<string>();
  for (const entry of glossary) {
    const key = entry.text.trim();
    if (!key) {
      continue;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }

  const threshold = draft.escalation?.threshold ?? ESCALATION_CATALOG.escalationThreshold.default;
  if (
    !Number.isInteger(threshold) ||
    threshold < ESCALATION_CATALOG.escalationThreshold.min ||
    threshold > ESCALATION_CATALOG.escalationThreshold.max
  ) {
    return false;
  }

  const handoffUrl = draft.checkoutMode.handoffUrl ?? '';
  if (draft.checkoutMode.mode === 'handoff' && !isOptionalHandoffUrlValid(handoffUrl)) {
    return false;
  }

  if (draft.checkoutMode.mode === 'inline') {
    const orderCompleteUrl = draft.checkoutMode.orderCompleteUrl ?? '';
    if (!isOptionalOrderCompleteUrlValid(orderCompleteUrl)) {
      return false;
    }
  }

  const roomScenes = activeRoomScenes(draft.productExperience);
  if (roomScenes && !isRoomScenesValid(roomScenes)) {
    return false;
  }

  if (!isWidgetPositionValid(draft.productExperience.widgetPosition)) {
    return false;
  }

  const agentSelector = toAgentSelector(draft.productExperience.agentSelector);
  const mobileLabelDraft = draft.productExperience.agentSelector?.mobile?.labelText ?? '';
  if (
    !isAgentSelectorHexValid(agentSelector.backgroundColor) ||
    !isAgentSelectorHexValid(agentSelector.leadingIconColor) ||
    !isAgentSelectorHexValid(agentSelector.mobile.backgroundColor) ||
    !isAgentSelectorHexValid(agentSelector.mobile.labelTextColor) ||
    !isAgentSelectorHexValid(agentSelector.mobile.leadingIconColor) ||
    !mobileLabelDraft.trim()
  ) {
    return false;
  }

  const rules = draft.rules ?? [];
  if (rules.length > MAX_RULES) {
    return false;
  }
  if (rules.some((rule) => !isRuleValid(rule))) {
    return false;
  }

  const personalities = draft.personalities ?? [];
  const hasInvalidPersonality = personalities.some((personality) => {
    const name = personality.displayName.trim();
    return (
      name.length < MIN_DISPLAY_NAME_LENGTH ||
      name.length > MAX_DISPLAY_NAME_LENGTH ||
      !personality.avatarSrc
    );
  });
  if (hasInvalidPersonality) {
    return false;
  }

  return Boolean(draft.promptManagement.customGreeting);
};

/** Build create POST body from the current draft (cleans glossary / optional contacts / handoff URL). */
export const draftToCreateSettings = (draft: EllaAgentDraft): AgenticAgentSettingsCreate => {
  const personalities = (draft.personalities ?? []).map(toPersonalityCreatePayload);
  const roomScenes = activeRoomScenes(draft.productExperience);
  return {
    promptManagement: toPromptManagementPayload(draft.promptManagement),
    voice: {
      pronunciationGlossary: completeGlossaryEntries(draft.voice?.pronunciationGlossary ?? []),
    },
    productExperience: {
      ...withoutRoomScenes(toProductExperience(draft.productExperience)),
      // Only when there are some, so creating settings still works against an MMS without the field.
      ...(roomScenes?.scenes.length ? { roomScenes } : {}),
    },
    engagement: toEngagement(draft.engagement),
    escalation: toEscalation(draft.escalation),
    checkoutMode: toCheckoutModePayload(draft.checkoutMode),
    rules: toRules(draft.rules),
    support: toSupport(draft.support),
    // Only when on, so creating settings still works against an MMS without the field.
    ...(draft.knowledgeBase?.enabled ? { knowledgeBase: { enabled: true } } : {}),
    ...(personalities.length > 0 ? { personalities } : {}),
  };
};

/** Diff baseline → draft into a PATCH body (only changed slices). */
export const buildPatchFromDraft = (
  baseline: EllaAgentDraft,
  draft: EllaAgentDraft,
): AgenticAgentSettingsPatch => {
  const body: AgenticAgentSettingsPatch = {};
  const base = draftForCompare(baseline);
  const next = draftForCompare(draft);

  if (!isEqual(base.promptManagement, next.promptManagement)) {
    body.promptManagement = toPromptManagementPayload(next.promptManagement);
  }

  if (!isEqual(base.voice, next.voice)) {
    body.voice = {
      pronunciationGlossary: next.voice?.pronunciationGlossary ?? [],
    };
  }

  if (!isEqual(base.productExperience, next.productExperience)) {
    const productExperience: NonNullable<AgenticAgentSettingsPatch['productExperience']> = {};

    if (base.productExperience.productDisplay !== next.productExperience.productDisplay) {
      productExperience.productDisplay = next.productExperience.productDisplay;
    }

    const nextPosition = toProductExperience(draft.productExperience).widgetPosition;
    const basePosition = base.productExperience.widgetPosition;
    if (!isEqual(basePosition, nextPosition)) {
      productExperience.widgetPosition = nextPosition ?? null;
    }

    const baseSelector = base.productExperience.agentSelector;
    const nextSelector = next.productExperience.agentSelector;
    if (!isEqual(baseSelector, nextSelector) && nextSelector) {
      const agentSelector: NonNullable<
        AgenticAgentSettingsPatch['productExperience']
      >['agentSelector'] = {};
      if (baseSelector?.backgroundColor !== nextSelector.backgroundColor) {
        agentSelector.backgroundColor = nextSelector.backgroundColor;
      }
      if (baseSelector?.showLeadingIcon !== nextSelector.showLeadingIcon) {
        agentSelector.showLeadingIcon = nextSelector.showLeadingIcon;
      }
      if (baseSelector?.leadingIcon !== nextSelector.leadingIcon) {
        agentSelector.leadingIcon = nextSelector.leadingIcon;
      }
      if (baseSelector?.leadingIconColor !== nextSelector.leadingIconColor) {
        agentSelector.leadingIconColor = nextSelector.leadingIconColor;
      }
      const mobilePatch: Partial<AgentSelectorMobileSettings> = {};
      if (baseSelector?.mobile.backgroundColor !== nextSelector.mobile.backgroundColor) {
        mobilePatch.backgroundColor = nextSelector.mobile.backgroundColor;
      }
      const baseLabelStored = mobileLabelTextForStorage(baseSelector?.mobile.labelText ?? '');
      const nextLabelStored = mobileLabelTextForStorage(nextSelector.mobile.labelText);
      if (baseLabelStored !== nextLabelStored) {
        mobilePatch.labelText = nextLabelStored;
      }
      if (baseSelector?.mobile.labelTextColor !== nextSelector.mobile.labelTextColor) {
        mobilePatch.labelTextColor = nextSelector.mobile.labelTextColor;
      }
      if (baseSelector?.mobile.showLeadingIcon !== nextSelector.mobile.showLeadingIcon) {
        mobilePatch.showLeadingIcon = nextSelector.mobile.showLeadingIcon;
      }
      if (baseSelector?.mobile.leadingIcon !== nextSelector.mobile.leadingIcon) {
        mobilePatch.leadingIcon = nextSelector.mobile.leadingIcon;
      }
      if (baseSelector?.mobile.leadingIconColor !== nextSelector.mobile.leadingIconColor) {
        mobilePatch.leadingIconColor = nextSelector.mobile.leadingIconColor;
      }
      if (Object.keys(mobilePatch).length > 0) {
        agentSelector.mobile = mobilePatch;
      }
      if (Object.keys(agentSelector).length > 0) {
        productExperience.agentSelector = agentSelector;
      }
    }

    if (Object.keys(productExperience).length > 0) {
      body.productExperience = productExperience;
    }
  }

  const roomScenes = changedRoomScenes(baseline, draft);
  if (roomScenes) {
    body.productExperience = { ...body.productExperience, roomScenes };
  }

  if (!isEqual(base.engagement, next.engagement)) {
    body.engagement = { ...next.engagement };
  }

  if (!isEqual(base.escalation, next.escalation)) {
    body.escalation = next.escalation;
  }

  if (!isEqual(base.checkoutMode, next.checkoutMode)) {
    const checkoutModePayload = toCheckoutModePayload(draft.checkoutMode);
    const clearsHandoffUrl =
      checkoutModePayload.mode === 'handoff' &&
      !checkoutModePayload.handoffUrl &&
      Boolean(base.checkoutMode.handoffUrl);
    const clearsOrderCompleteUrl =
      checkoutModePayload.mode === 'inline' &&
      !checkoutModePayload.orderCompleteUrl &&
      Boolean(base.checkoutMode.orderCompleteUrl);
    body.checkoutMode = {
      ...checkoutModePayload,
      ...(clearsHandoffUrl ? { handoffUrl: null } : {}),
      ...(clearsOrderCompleteUrl ? { orderCompleteUrl: null } : {}),
    };
  }

  if (!isEqual(base.rules ?? [], next.rules ?? [])) {
    body.rules = next.rules ?? [];
  }

  if (!isEqual(base.support, next.support)) {
    body.support = next.support;
  }

  if (!isEqual(toKnowledgeBase(base.knowledgeBase), toKnowledgeBase(next.knowledgeBase))) {
    body.knowledgeBase = toKnowledgeBase(next.knowledgeBase);
  }

  const personalities = toPersonalitiesPatch(baseline.personalities, draft.personalities);
  if (personalities) {
    body.personalities = personalities;
  }

  return body;
};
